// ── Estimating Assistants ─────────────────────────────────────────────────────
// Standalone, no-project ("one-off") AI review tools that live under the
// Estimating Assistant page. Each is a PORT of a turnover-module review feature
// (contract review = Section 2, schedule review = Section 9, correspondence =
// Section 14), decoupled from turnover. Two execution flows:
//
//   • ChunkedReviewAssistant (contract) — drives the server-side chunk →
//     analyze → synthesize pipeline (cs-est-takeoff-api/assistant-review) via
//     202-kickoff + status polling, so large documents get the same complete
//     review as turnover. Staging rows are ephemeral (cleared on each new run;
//     see db/26_assistant_reviews.sql).
//   • MarkdownReviewAssistant / ScheduleReviewAssistant (correspondence,
//     schedule) — a single in-browser call through the shared claude-fn proxy;
//     nothing saved server-side.
//
// Both flows reuse the SystemPrompts rows (sp_GetSystemPrompt @category=...)
// and the AssistantMarkdown renderer (an estimating-owned copy) so output
// matches the real modules.
//
// Per the CLAUDE.md module-boundary rule, nothing here imports from
// src/pages/turnover/* and nothing here writes to turnover-scoped tables.

import { useState, useRef, useEffect } from "react";
import { sqlRead } from "../../../utils/sqlClient";
import { callClaudeWithMeta, callClaudeConversation } from "../../../utils/claudeClient";
import { ASSISTANT_REVIEW_URL } from "../../../config/api";
import { msalInstance } from "../../../auth/msalInstance";
import { authHeader } from "../../../utils/apiToken";
import AssistantMarkdown from "./AssistantMarkdown";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// A per-browser-session id groups a user's review runs so the backend can clear
// their previous staging rows on each new upload (see sp_CreateAssistantReviewJob).
function getSessionId() {
  let s = sessionStorage.getItem("assistantSessionId");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem("assistantSessionId", s);
  }
  return s;
}

// Per-call output cap. The proxy is non-streaming, so a single call must finish
// within the Azure gateway timeout (~230s) — keep this modest and rely on the
// continuation loop below to assemble longer reviews across multiple calls.
const PER_CALL_MAX_TOKENS = 8000;
// Safety cap on continuation rounds so a runaway response can't loop forever.
const MAX_CONTINUATIONS = 3;

const IMAGE_EXTS = ["jpg", "jpeg", "png", "gif", "webp"];
const ACCEPT = ".pdf,.jpg,.jpeg,.png";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = () => reject(new Error("File read failed"));
    reader.readAsDataURL(file);
  });
}

// Build the Claude content block for an uploaded file (PDF → document, image → image).
function fileContentBlock(file, base64) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (IMAGE_EXTS.includes(ext)) {
    return { type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: base64 } };
  }
  return { type: "document", source: { type: "base64", media_type: "application/pdf", data: base64 } };
}

// Fetch the assembled system prompt for a category from the SystemPrompts table
// (the SAME prompt the turnover module uses, editable in Help → AI System Prompts).
async function fetchPrompt(category) {
  const rows = await sqlRead(`EXEC sp_GetSystemPrompt @category='${category}'`);
  const full = rows?.[0]?.FullPrompt;
  if (!full) throw new Error(`No active system prompt found for "${category}".`);
  return full;
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Shared upload drop zone ───────────────────────────────────────────────────
// `accept`/`hint` are overridable because the chunked backend flow is PDF-only
// (the PDF chunker can't split images) while the in-browser flow takes both.
function FileDropZone({ file, onPick, disabled, accept = ACCEPT, hint = "PDF or image · nothing is saved — this is a one-off review" }) {
  const inputRef = useRef(null);
  return (
    <>
      <div
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); if (!disabled && e.dataTransfer?.files?.length) onPick(e.dataTransfer.files[0]); }}
        onClick={() => !disabled && inputRef.current?.click()}
        style={{
          border: "2px dashed var(--cs-border2)", borderRadius: "var(--radius-lg)",
          padding: 24, textAlign: "center", cursor: disabled ? "default" : "pointer",
          marginBottom: 12, opacity: disabled ? 0.6 : 1,
        }}
      >
        <input ref={inputRef} type="file" style={{ display: "none" }} accept={accept}
          onChange={e => { if (e.target.files?.length) onPick(e.target.files[0]); e.target.value = ""; }} />
        {file ? (
          <>
            <div style={{ fontSize: 22, marginBottom: 6 }}>📄</div>
            <div style={{ fontWeight: 500, fontSize: 14, color: "var(--text)", marginBottom: 2 }}>{file.name}</div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>
              {(file.size / 1024).toFixed(0)} KB · click to replace
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 28, marginBottom: 8 }}>📎</div>
            <div style={{ fontWeight: 500, fontSize: 14, color: "var(--text)", marginBottom: 4 }}>Drop a document here</div>
            <div style={{ fontSize: 12, color: "var(--text3)" }}>{hint}</div>
          </>
        )}
      </div>
    </>
  );
}

function ResultActions({ text, filename }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
      <button
        className="reset-link"
        onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
      >
        {copied ? "✓ Copied" : "Copy text"}
      </button>
      <button className="reset-link" onClick={() => downloadText(filename, text)}>Download</button>
    </div>
  );
}

// ── Generic markdown-output review assistant (Contract + Correspondence) ───────
function MarkdownReviewAssistant({
  category, title, subtitle, runLabel, placeholderTip, loadingBarRef,
}) {
  const [file, setFile]       = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult]   = useState(null); // { text, truncated }
  const [error, setError]     = useState(null);

  const run = async () => {
    if (!file) return;
    setRunning(true); setResult(null); setError(null); setProgress("");
    if (loadingBarRef?.current) loadingBarRef.current.classList.add("active");
    try {
      const [system, base64] = await Promise.all([fetchPrompt(category), fileToBase64(file)]);
      // The original request (document + instruction) is resent on every
      // continuation so the model keeps the source in view for accurate
      // citations. We append its own partial output and ask it to continue.
      const firstTurn = [
        fileContentBlock(file, base64),
        { type: "text", text: "Review the attached document per your instructions." },
      ];

      let full = "";
      let truncated = false;
      for (let round = 0; round <= MAX_CONTINUATIONS; round++) {
        const messages = round === 0
          ? [{ role: "user", content: firstTurn }]
          : [
              { role: "user", content: firstTurn },
              { role: "assistant", content: full },
              { role: "user", content: "Continue exactly where you left off. Do not repeat any content you already produced." },
            ];
        if (round > 0) setProgress(`Continuing the review (part ${round + 1})…`);
        const { text, stopReason } = await callClaudeConversation(system, messages, PER_CALL_MAX_TOKENS);
        full += text || "";
        if (stopReason !== "max_tokens") { truncated = false; break; }
        truncated = true; // still truncated after this round; loop unless capped
      }

      if (!full.trim()) throw new Error("The assistant returned an empty response — try again.");
      setResult({ text: full, truncated });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRunning(false); setProgress("");
      if (loadingBarRef?.current) loadingBarRef.current.classList.remove("active");
    }
  };

  const reset = () => { setFile(null); setResult(null); setError(null); };
  const outName = `${category}-${(file?.name || "review").replace(/\.[^.]+$/, "")}.md`;

  return (
    <div className="panel-content">
      <div className="panel-title">{title}</div>
      <div className="panel-sub">{subtitle}</div>

      <FileDropZone file={file} onPick={setFile} disabled={running} />
      {placeholderTip && <div className="tip" style={{ marginBottom: 12 }}>{placeholderTip}</div>}

      <button className="go-btn" onClick={run} disabled={running || !file}>
        {running ? <><div className="spinner" />{progress || "Reviewing…"}</> : `${runLabel} →`}
      </button>

      {error && (
        <div className="results">
          <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{error}</div></div>
        </div>
      )}

      {result && (
        <div className="results">
          {result.truncated && (
            <div style={{ padding: "9px 12px", marginBottom: 10, background: "var(--amber-lt, #fffbeb)", border: "1px solid rgba(180,120,0,0.25)", borderRadius: "var(--radius)", fontSize: 12, color: "var(--text)" }}>
              ⚠️ This document was long enough that the review was still being cut off after {MAX_CONTINUATIONS + 1} passes. The text below is complete up to that point — for a document this size, run the full review inside a turnover project.
            </div>
          )}
          <ResultActions text={result.text} filename={outName} />
          <div className="card">
            <div className="card-body"><AssistantMarkdown text={result.text} /></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button className="reset-link" onClick={reset}>Review another document</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Chunked backend review assistant (Contract) ────────────────────────────────
// Unlike MarkdownReviewAssistant (single in-browser call), this drives the
// server-side chunk -> analyze -> synthesize pipeline so large documents get the
// same comprehensive, structured review as the turnover module. It kicks off a
// background job (ASSISTANT_REVIEW_URL), polls sp_GetAssistantReviewStatus, then
// renders the synthesized sections from sp_GetAssistantReviewResult. Ephemeral —
// the staging rows are cleared on the next upload.
const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 225; // ~15 min ceiling
// If the job row never appears, the async kickoff was lost (the 202 ack
// fire-and-forgets the heavy pass) — fail fast instead of burning all 15 min.
const MAX_POLLS_WITHOUT_JOB = 30; // ~2 min
// The PDF chunker rejects >50 MB server-side; catch it before uploading.
const MAX_PDF_BYTES = 50 * 1024 * 1024;

function ChunkedReviewAssistant({
  reviewType, category, title, subtitle, runLabel, placeholderTip, loadingBarRef,
}) {
  const [file, setFile]       = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult]   = useState(null); // { text }
  const [error, setError]     = useState(null);

  // The backend pipeline splits the document with the PDF chunker, so only
  // PDFs are reviewable here (images go through the in-browser assistants).
  const pickFile = (f) => {
    setError(null);
    if (!/\.pdf$/i.test(f.name)) { setError("This assistant reviews PDF documents only."); return; }
    if (f.size > MAX_PDF_BYTES) { setError("This PDF is over the 50 MB limit — split it and review the parts separately."); return; }
    setFile(f);
  };

  // Poll loops keep running after unmount if we don't stop them; this ref lets
  // the effect cleanup cancel an in-flight review so we never setState on an
  // unmounted component (mirrors the poll-cleanup rule in TurnoverDetail).
  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  const run = async () => {
    if (!file) return;
    cancelledRef.current = false;
    setRunning(true); setResult(null); setError(null); setProgress("Uploading document…");
    if (loadingBarRef?.current) loadingBarRef.current.classList.add("active");
    try {
      if (!ASSISTANT_REVIEW_URL) throw new Error("Review service is not configured (missing VITE_ASSISTANT_REVIEW_URL).");
      const base64 = await fileToBase64(file);
      const jobId = (crypto.randomUUID && crypto.randomUUID()) || `job-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const sessionId = getSessionId();

      const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      const res = await fetch(ASSISTANT_REVIEW_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json", ...(await authHeader()) },
        body: JSON.stringify({
          job_id: jobId, session_id: sessionId, review_type: reviewType,
          pdf_base64: base64, filename: file.name,
          submitted_by: account?.name || account?.username || "",
        }),
      });
      // 202 = accepted (expected); any other non-2xx is a real failure.
      if (!res.ok && res.status !== 202) {
        const t = await res.text().catch(() => "");
        throw new Error(`Upload failed (${res.status})${t ? `: ${t.slice(0, 200)}` : ""}`);
      }

      setProgress("Analyzing the document…");
      const started = Date.now();
      let done = false;
      let jobSeen = false;
      for (let i = 0; i < MAX_POLLS && !cancelledRef.current; i++) {
        await sleep(POLL_INTERVAL_MS);
        if (cancelledRef.current) return;
        let rows;
        try {
          rows = await sqlRead(`EXEC sp_GetAssistantReviewStatus @job_id='${jobId}'`);
        } catch {
          continue; // transient read blip — keep polling
        }
        const st = rows?.[0];
        if (!st) {
          // Job row not written yet. If it never shows up, the async kickoff
          // was lost — bail out instead of polling the full ceiling.
          if (!jobSeen && i >= MAX_POLLS_WITHOUT_JOB) {
            throw new Error("The review never started (the service didn't pick up the job). Try again.");
          }
          continue;
        }
        jobSeen = true;
        if (st.Status === "Failed") throw new Error(st.ErrorMessage || "The review failed. Try again.");
        if (st.Status === "Complete") { done = true; break; }
        setProgress(`Analyzing the document… (${Math.round((Date.now() - started) / 1000)}s)`);
      }
      if (cancelledRef.current) return;
      if (!done) throw new Error("The review is taking longer than expected. Try again, or split a very large document.");

      const parts = await sqlRead(`EXEC sp_GetAssistantReviewResult @job_id='${jobId}'`);
      const md = (parts || []).map((p) => p.PartText || "").join("\n\n").trim();
      if (!md) throw new Error("The review produced no output. Try again.");
      setResult({ text: md });
    } catch (e) {
      if (!cancelledRef.current) setError(e.message || String(e));
    } finally {
      if (!cancelledRef.current) { setRunning(false); setProgress(""); }
      if (loadingBarRef?.current) loadingBarRef.current.classList.remove("active");
    }
  };

  const reset = () => { setFile(null); setResult(null); setError(null); };
  const outName = `${category}-${(file?.name || "review").replace(/\.[^.]+$/, "")}.md`;

  return (
    <div className="panel-content">
      <div className="panel-title">{title}</div>
      <div className="panel-sub">{subtitle}</div>

      <FileDropZone
        file={file} onPick={pickFile} disabled={running}
        accept=".pdf" hint="PDF only · nothing is saved — this is a one-off review"
      />
      {placeholderTip && <div className="tip" style={{ marginBottom: 12 }}>{placeholderTip}</div>}

      <button className="go-btn" onClick={run} disabled={running || !file}>
        {running ? <><div className="spinner" />{progress || "Reviewing…"}</> : `${runLabel} →`}
      </button>

      {running && (
        <div className="tip" style={{ marginTop: 10 }}>
          Large contracts are read in sections and can take a few minutes — keep this tab open.
        </div>
      )}

      {error && (
        <div className="results">
          <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{error}</div></div>
        </div>
      )}

      {result && (
        <div className="results">
          <ResultActions text={result.text} filename={outName} />
          <div className="card">
            <div className="card-body"><AssistantMarkdown text={result.text} /></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button className="reset-link" onClick={reset}>Review another document</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Schedule review assistant (JSON milestones → table) ────────────────────────
// Deliberate variant of utils/format.js fmtDate: dates here come from AI JSON
// extraction, so an unparseable value ("TBD", a range) is shown verbatim
// instead of "—", and the browser's own locale is used.
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d + (String(d).length === 10 ? "T00:00:00" : ""));
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function ScheduleReviewAssistant({ loadingBarRef }) {
  const [file, setFile]       = useState(null);
  const [running, setRunning] = useState(false);
  const [milestones, setMilestones] = useState(null);
  const [error, setError]     = useState(null);

  const run = async () => {
    if (!file) return;
    setRunning(true); setMilestones(null); setError(null);
    if (loadingBarRef?.current) loadingBarRef.current.classList.add("active");
    try {
      const [system, base64] = await Promise.all([fetchPrompt("schedule_review"), fileToBase64(file)]);
      const userContent = [
        fileContentBlock(file, base64),
        { type: "text", text: "Extract the CleanSpace-relevant milestones from this schedule per your instructions. Return only the JSON object." },
      ];
      const { text } = await callClaudeWithMeta(system, userContent, 8000);
      const first = text.indexOf("{"); const last = text.lastIndexOf("}");
      if (first === -1 || last === -1) throw new Error("The assistant did not return schedule JSON — try again.");
      const parsed = JSON.parse(text.slice(first, last + 1));
      const rows = Array.isArray(parsed.milestones) ? parsed.milestones : [];
      if (rows.length === 0) throw new Error("No milestones were found in this schedule.");
      setMilestones(rows);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRunning(false);
      if (loadingBarRef?.current) loadingBarRef.current.classList.remove("active");
    }
  };

  const reset = () => { setFile(null); setMilestones(null); setError(null); };

  const exportText = () => (milestones || [])
    .map(m => `${m.cs_relevant ? "★ " : "  "}${m.label}\t${m.start_date || ""}\t${m.end_date || ""}\t${m.notes || ""}`)
    .join("\n");

  return (
    <div className="panel-content">
      <div className="panel-title">Schedule Review Assistant</div>
      <div className="panel-sub">
        Upload a GC construction schedule (Primavera P6 / MS Project PDF). The assistant extracts the milestones
        that matter to CleanSpace scope. One-off — nothing is saved.
      </div>

      <FileDropZone file={file} onPick={setFile} disabled={running} />
      <div className="tip" style={{ marginBottom: 12 }}>
        Native-PDF schedules work best. Image-only / scanned schedules may extract less — run those inside a
        turnover project, which does an extra image pass.
      </div>

      <button className="go-btn" onClick={run} disabled={running || !file}>
        {running ? <><div className="spinner" />Extracting milestones…</> : "Extract milestones →"}
      </button>

      {error && (
        <div className="results">
          <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{error}</div></div>
        </div>
      )}

      {milestones && (
        <div className="results">
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--text3)" }}>
              {milestones.length} milestone{milestones.length !== 1 ? "s" : ""} · ★ = CleanSpace-relevant
            </span>
            <div style={{ flex: 1 }} />
            <button className="reset-link" onClick={() => navigator.clipboard?.writeText(exportText())}>Copy</button>
          </div>
          <div className="card">
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 560 }}>
                <thead>
                  <tr style={{ background: "var(--tint-blue)" }}>
                    <th style={schTh}>Milestone</th>
                    <th style={{ ...schTh, whiteSpace: "nowrap" }}>Start</th>
                    <th style={{ ...schTh, whiteSpace: "nowrap" }}>Finish</th>
                    <th style={schTh}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {milestones.map((m, i) => (
                    <tr key={i} style={{ background: m.cs_relevant ? "var(--tint-green)" : (i % 2 ? "var(--surface2)" : "#fff") }}>
                      <td style={{ ...schTd, fontWeight: m.cs_relevant ? 600 : 400 }}>
                        {m.cs_relevant && <span style={{ color: "#16a34a", marginRight: 5 }}>★</span>}
                        {m.label}
                      </td>
                      <td style={{ ...schTd, whiteSpace: "nowrap" }}>{fmtDate(m.start_date)}</td>
                      <td style={{ ...schTd, whiteSpace: "nowrap" }}>{fmtDate(m.end_date)}</td>
                      <td style={{ ...schTd, color: "var(--text2)" }}>{m.notes || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
            <button className="reset-link" onClick={reset}>Review another schedule</button>
          </div>
        </div>
      )}
    </div>
  );
}

const schTh = { padding: "7px 10px", borderBottom: "2px solid #bae6fd", textAlign: "left", fontWeight: 700, color: "#0369a1" };
const schTd = { padding: "7px 10px", borderBottom: "1px solid #e0f2fe", color: "var(--text)", verticalAlign: "top", lineHeight: 1.5 };

// ── Exported panels ────────────────────────────────────────────────────────────
export function ContractReviewAssistant({ loadingBarRef }) {
  return (
    <ChunkedReviewAssistant
      reviewType="contract"
      category="contract_review"
      title="Contract Review Assistant"
      subtitle="Upload a contract or subcontract PDF for a CleanSpace-calibrated review — commercial terms, insurance, risk flags, department routing, and next steps. The document is read in sections, so even large contracts get a complete, structured review. One-off — nothing is saved."
      runLabel="Review contract"
      placeholderTip="Handles large, multi-page contracts. Nothing is saved — for reviews with saved history and department sign-off, use the turnover module."
      loadingBarRef={loadingBarRef}
    />
  );
}

export function CorrespondenceReviewAssistant({ loadingBarRef }) {
  return (
    <MarkdownReviewAssistant
      category="correspondence"
      title="Correspondence Review Assistant"
      subtitle="Upload correspondence (emails, meeting notes, scope-clarification or client-commitment letters) to extract scope clarifications, exclusions, assumptions, client commitments, open items, and conflicts. One-off — nothing is saved."
      runLabel="Review correspondence"
      loadingBarRef={loadingBarRef}
    />
  );
}
